import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';

import { appConfig } from '@/config/app.config';
import { ENDPOINT_ADMIN } from '@/utils/endpoints';

export const BASE_URL = appConfig.api.baseUrl;

let inMemoryAccessToken: string | null = null;

const TOKEN_EXPIRY_STORAGE_KEY = 'token_expiry';
const REFRESH_TOKEN_STORAGE_KEY = 'jumbo-refresh';

type TDecodedToken = {
  exp?: number;
};

const decodeAndStoreTokenExpiry = (token: string): void => {
  try {
    const decoded = jwtDecode<TDecodedToken>(token);
    if (decoded.exp) {
      if (globalThis.window !== undefined) {
        globalThis.localStorage.setItem(
          TOKEN_EXPIRY_STORAGE_KEY,
          decoded.exp.toString()
        );
      }
    }
  } catch {
    // Silently fail if decode fails
  }
};

export const getAccessToken = (): string | null => {
  return inMemoryAccessToken;
};

export const setAccessToken = (token: string): void => {
  inMemoryAccessToken = token;
  decodeAndStoreTokenExpiry(token);
};

export const clearAccessToken = (): void => {
  inMemoryAccessToken = null;
  if (globalThis.window !== undefined) {
    globalThis.localStorage.removeItem(TOKEN_EXPIRY_STORAGE_KEY);
    globalThis.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  }
};

const getRefreshToken = (): string | null => {
  if (globalThis.window === undefined) {
    return null;
  }
  return globalThis.localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
};

const setRefreshToken = (token: string): void => {
  if (globalThis.window !== undefined) {
    globalThis.localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, token);
  }
};

export const authClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

export const client = axios.create({
  baseURL: BASE_URL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

const TOKEN_EXPIRY_MARGIN_SECONDS = 60;

const isTokenExpiring = (): boolean => {
  if (globalThis.window === undefined) {
    return false;
  }

  try {
    const storedExpiry = globalThis.localStorage.getItem(
      TOKEN_EXPIRY_STORAGE_KEY
    );
    if (!storedExpiry) return false;

    const expiryInSeconds = Number.parseInt(storedExpiry, 10);
    if (Number.isNaN(expiryInSeconds)) return false;

    const nowInSeconds = Math.floor(Date.now() / 1000);
    const secondsLeft = expiryInSeconds - nowInSeconds;
    return secondsLeft > 0 && secondsLeft <= TOKEN_EXPIRY_MARGIN_SECONDS;
  } catch {
    return false;
  }
};

let isRefreshing = false;

let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: Error) => void;
}> = [];

const flushQueue = (error: Error | null, token: string | null) => {
  failedQueue.forEach(p => {
    if (error) p.reject(error);
    else if (token) p.resolve(token);
  });

  failedQueue = [];
};

const refreshTokenOnly = async (): Promise<string> => {
  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;

  try {
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      clearAccessToken();
      throw new Error('No refresh token found');
    }

    const { data: response } = await authClient.post<{
      statusCode: 200;
      message: string;
      data: {
        jumboAccessToken: string;
        refreshToken: string;
        role: string;
      };
    }>(ENDPOINT_ADMIN.googleRefresh, { refreshToken });

    if (response.statusCode !== 200 || !response.data?.jumboAccessToken) {
      clearAccessToken();
      throw new Error('Token refresh failed');
    }

    const newAccessToken = response.data.jumboAccessToken;
    const newRefreshToken = response.data.refreshToken;

    setAccessToken(newAccessToken);

    if (newRefreshToken) {
      setRefreshToken(newRefreshToken);
    }

    flushQueue(null, newAccessToken);
    isRefreshing = false;

    return newAccessToken;
  } catch (err) {
    flushQueue(err as Error, null);
    isRefreshing = false;

    clearAccessToken();

    if (
      globalThis.window !== undefined &&
      globalThis.window.location.pathname !== '/login'
    ) {
      globalThis.window.location.href = '/login';
    }

    throw err;
  }
};

const refreshAccessToken = async (
  originalRequest: InternalAxiosRequestConfig & { _retry?: boolean }
) => {
  originalRequest._retry = true;
  const newToken = await refreshTokenOnly();
  originalRequest.headers.Authorization = `Bearer ${newToken}`;
  return client(originalRequest);
};

const AUTH_PATH_SEGMENT = '/auth/';
const NOVO_SYNC_IDENTITY_PATH = '/auth/sync-identity';

const shouldSkipAuthHeader = (url?: string) =>
  Boolean(
    url?.includes(AUTH_PATH_SEGMENT) && !url.includes(NOVO_SYNC_IDENTITY_PATH)
  );

client.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    const is401 = error.response?.status === 401;
    const isAuthRequest = shouldSkipAuthHeader(originalRequest.url);

    if (is401) {
      if (originalRequest._retry || isAuthRequest) {
        clearAccessToken();
        if (
          globalThis.window !== undefined &&
          globalThis.window.location.pathname !== '/login'
        ) {
          globalThis.window.location.href = '/login';
        }
        throw new Error('Authentication failed');
      }

      try {
        return await refreshAccessToken(originalRequest);
      } catch {
        const msg =
          (error.response?.data as { message?: string })?.message ||
          error.message ||
          'Authentication failed';
        throw new Error(msg);
      }
    }

    const message =
      (error.response?.data as { message?: string })?.message ||
      (error.response?.data as { msg?: string })?.msg ||
      error.message ||
      'Something went wrong';

    throw new Error(message);
  }
);

client.interceptors.request.use(
  async config => {
    if (globalThis.window === undefined) {
      return config;
    }

    if (!(config.data instanceof FormData)) {
      config.headers.set('Content-Type', 'application/json');
    }

    const isAuthRequest = shouldSkipAuthHeader(config.url);
    if (isAuthRequest) return config;

    const shouldUseDiffEndpoints =
      import.meta.env.VITE_SHOULD_USE_DIFF_ENDPOINTS === 'true';

    if (shouldUseDiffEndpoints && !config.baseURL) {
      const isFantasyRequest = config.url?.includes('/v1/fantasy');

      if (isFantasyRequest) {
        const fantasyBaseURL =
          import.meta.env.VITE_FANTASY_API_BASE_URL || BASE_URL;
        config.baseURL = fantasyBaseURL;
      } else {
        config.baseURL = BASE_URL;
      }
    }

    const token = getAccessToken();

    if (!token || isTokenExpiring()) {
      const newToken = await refreshTokenOnly();
      config.headers.set('Authorization', `Bearer ${newToken}`);
      return config;
    }

    config.headers.set('Authorization', `Bearer ${token}`);
    return config;
  },
  error => Promise.reject(error)
);
